import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { inputCls, PageHeader } from "./shared";

const empty = { name: "", category: "Hair", price: "", duration_min: 60, description: "", active: true };
const CATEGORIES = ["Hair", "Skin", "Makeup", "Nails", "Spa", "Packages", "Academy"];

export default function ServicesAdmin() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/services", { params: { all: true } }).then(({ data }) => setServices(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, category: s.category, price: s.price, duration_min: s.duration_min, description: s.description || "", active: s.active }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    const payload = { ...form, price: Number(form.price), duration_min: Number(form.duration_min) };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? "Service updated" : "Service added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Service deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/services/${s.id}`, { name: s.name, category: s.category, price: s.price, duration_min: s.duration_min, description: s.description || "", active: !s.active });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="services-page">
      <PageHeader title="Services & Pricing" subtitle="What your salon offers — shown live on the website."
        action={
          <button onClick={openNew} data-testid="add-service-btn"
            className="btn-lift bg-[#0A3F2F] text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-[#0d5540]">
            <Plus className="w-4 h-4" /> Add Service
          </button>
        }
      />
      <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
              <th className="px-4 py-3 font-semibold">Service</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Price</th>
              <th className="px-4 py-3 font-semibold">Duration</th>
              <th className="px-4 py-3 font-semibold">Visible</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="services-table-body">
            {services.map((s) => (
              <tr key={s.id} className="border-b border-[#E5E7E5] last:border-0 hover:bg-[#FAFAF8]" data-testid={`service-row-${s.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#1C1C1C]">{s.name}</p>
                  <p className="text-xs text-[#5C6A63] max-w-xs truncate">{s.description}</p>
                </td>
                <td className="px-4 py-3"><span className="text-xs bg-[#C5A059]/15 text-[#8a6d33] px-2.5 py-1 rounded-full font-semibold">{s.category}</span></td>
                <td className="px-4 py-3 font-semibold text-[#0A3F2F]">₹{s.price.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-[#5C6A63]">{s.duration_min ? `${s.duration_min} min` : "—"}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(s)} data-testid={`toggle-service-${s.id}`}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${s.active ? "bg-emerald-600/10 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                    {s.active ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`} className="text-gray-400 hover:text-[#0A3F2F] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(s.id)} data-testid={`delete-service-${s.id}`} className="text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="service-form">
            <input data-testid="service-name-input" className={inputCls} placeholder="Service name *" value={form.name} onChange={set("name")} />
            <select data-testid="service-category-select" className={inputCls} value={form.category} onChange={set("category")}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="service-price-input" type="number" className={inputCls} placeholder="Price (₹) *" value={form.price} onChange={set("price")} />
              <input data-testid="service-duration-input" type="number" className={inputCls} placeholder="Duration (min)" value={form.duration_min} onChange={set("duration_min")} />
            </div>
            <textarea data-testid="service-description-input" className={inputCls} rows={2} placeholder="Description" value={form.description} onChange={set("description")} />
            <button type="submit" data-testid="service-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              {editing ? "Save Changes" : "Add Service"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
